import { createPlugin } from '@customerio/dryad';
import type { ValidationIssue } from '@customerio/dryad';
import { isRecord } from '@/utils/objects';
import { ConditionType } from './condition-types';
import type { NewsletterRecipientsFilter } from './wire-types';

// The proto `{ newsletter_recipients: { id, templateID? } }` leaf, negation as
// a `{ not: ... }` wrapper. Note the key rename against the flat legacy-array
// dialect, where this condition is `{ type: 'newsletter_recipients',
// newsletter_id, template_id }` with snake_case keys. The proto side takes
// `id` from `newsletter_id` and is camelCase `templateID`, omitted when no
// variation is chosen.

/**
 * A newsletter (optionally narrowed to one variation's template) the person
 * did or did not receive. Ids are `number | string`: the producers write
 * numbers, but ids that arrived on the wire round-trip verbatim (a URL
 * round-trip can leave them as strings). New picks are coerced to numbers in
 * the row's pick handler, never in the plugin's serialize.
 */
export type NewsletterRecipientsConditionState = {
  newsletterId: number | string | null;
  templateId: number | string | null;
  negate: boolean;
};

/**
 * The invalid state must be visible because it blocks the host's commit, so
 * the row renders a message on the picker — with its own wording, since this
 * one can't follow the taxonomy from inside a pure `validate`.
 */
export function validateNewsletterRecipients(state: NewsletterRecipientsConditionState): Required<ValidationIssue>[] {
  if (state.newsletterId == null) {
    return [{ message: 'Select a newsletter', field: 'newsletterId' }];
  }
  return [];
}

export function createDefaultNewsletterRecipientsState(): NewsletterRecipientsConditionState {
  return { newsletterId: null, templateId: null, negate: false };
}

type WireNewsletterRecipients = { id: number | string; templateID?: number | string };

// fallow-ignore-next-line complexity -- a flat sequence of wire shape guards; splitting hurts readability
function newsletterRecipientsProperty(json: unknown): WireNewsletterRecipients | null {
  if (!isRecord(json) || !isRecord(json.newsletter_recipients)) {
    return null;
  }
  const { id, templateID } = json.newsletter_recipients;
  if (typeof id !== 'number' && typeof id !== 'string') {
    return null;
  }
  if (templateID != null && typeof templateID !== 'number' && typeof templateID !== 'string') {
    return null;
  }
  if (templateID == null) {
    return { id };
  }
  return { id, templateID };
}

function unwrapNewsletterRecipientsNode(
  json: unknown,
): { recipients: WireNewsletterRecipients; negated: boolean } | null {
  const direct = newsletterRecipientsProperty(json);
  if (direct != null) {
    return { recipients: direct, negated: false };
  }
  const negated = isRecord(json) ? newsletterRecipientsProperty(json.not) : null;
  if (negated != null) {
    return { recipients: negated, negated: true };
  }
  return null;
}

function parseNewsletterRecipientsFilter(json: unknown): NewsletterRecipientsConditionState | null {
  const unwrapped = unwrapNewsletterRecipientsNode(json);
  if (unwrapped == null) {
    return null;
  }
  return {
    newsletterId: unwrapped.recipients.id,
    templateId: unwrapped.recipients.templateID ?? null,
    negate: unwrapped.negated,
  };
}

function serializeNewsletterRecipientsFilter(
  state: NewsletterRecipientsConditionState,
): NewsletterRecipientsFilter | { not: NewsletterRecipientsFilter } {
  const recipients: WireNewsletterRecipients = { id: state.newsletterId ?? '' };
  if (state.templateId != null) {
    recipients.templateID = state.templateId;
  }
  const filter: NewsletterRecipientsFilter = { newsletter_recipients: recipients };
  if (state.negate) {
    return { not: filter };
  }
  return filter;
}

export const peopleFilterNewsletterRecipientsPlugin = createPlugin({
  type: ConditionType.NewsletterRecipients,
  label: 'Newsletter',
  parse: parseNewsletterRecipientsFilter,
  serialize: serializeNewsletterRecipientsFilter,
  validate: validateNewsletterRecipients,
  createDefault: createDefaultNewsletterRecipientsState,
});
